<template>
  <validation-observer ref="observer">
    <v-form ref="form" @submit.prevent="submitCreateUser">
      <v-card-text>
        <validation-provider
          v-slot="{ errors }"
          name="username"
          :rules="rules.username"
        >
          <v-text-field
            v-model="formData.username"
            :error-messages="errors"
            label="Username*"
            outlined
            dense
            required
          ></v-text-field>
        </validation-provider>
        <v-row class="mt-1">
          <v-col class="py-0" cols="12" md="6">
            <validation-provider
              v-slot="{ errors }"
              name="first_name"
              :rules="rules.first_name"
            >
              <v-text-field
                v-model="formData.first_name"
                :error-messages="errors"
                label="ชื่อ*"
                outlined
                dense
                required
              ></v-text-field>
            </validation-provider>
          </v-col>
          <v-col class="py-0" cols="12" md="6">
            <validation-provider
              v-slot="{ errors }"
              name="last_name"
              :rules="rules.last_name"
            >
              <v-text-field
                v-model="formData.last_name"
                :error-messages="errors"
                label="สกุล*"
                outlined
                dense
                required
              ></v-text-field>
            </validation-provider>
          </v-col>
        </v-row>
        <div class="text-h6 mt-4">สถานะ*</div>
        <validation-provider
          v-slot="{ errors }"
          name="selectRoles"
          :rules="rules.selectRoles"
        >
          <v-chip-group
            v-model="selectRoles"
            column
            multiple
            @change="onUserRoleChange"
          >
            <v-chip
              v-for="(role, index) in roles"
              :key="`role_${index}`"
              filter
              outlined
            >
              {{ role.name }}
            </v-chip>
          </v-chip-group>
          <p
            v-for="(error, index) in errors"
            :key="`errtxt_${index}`"
            class="danger--text mb-0"
          >
            <small>{{ error }}</small>
          </p>
        </validation-provider>
        <v-row class="mt-3">
          <v-col v-if="under_faculty" class="py-0" cols="12" :md="under_major ? 6 : 12">
            <validation-provider
              v-slot="{ errors }"
              vid="faculty_id"
              name="faculty_id"
              :rules="rules.faculty_id"
            >
              <v-select
                v-model="formData.faculty_id"
                :error-messages="errors"
                label="คณะ"
                :items="faculties"
                item-value="id"
                item-text="name"
                outlined
                dense
                @change="onFacultyChange"
              >
              </v-select>
            </validation-provider>
          </v-col>
          <v-col v-if="under_major" class="py-0" cols="12" md="6">
            <validation-provider
              v-slot="{ errors }"
              vid="major_id"
              name="major_id"
              :rules="rules.major_id"
            >
              <v-select
                v-model="formData.major_id"
                :error-messages="errors"
                label="สาขา"
                :items="option_majors"
                item-value="id"
                item-text="name"
                outlined
                dense
                :disabled="!(under_faculty && formData.faculty_id)"
              >
              </v-select>
            </validation-provider>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="onCancel" text> ยกเลิก </v-btn>
        <v-btn color="primary" :loading="isLoading" type="submit">
          บันทึก
        </v-btn>
      </v-card-actions>
    </v-form>
  </validation-observer>
</template>

<script>
import { mapGetters } from "vuex"
import _ from "lodash"
import {
  GET_YEARS,
  GET_FACULTIES,
  GET_MAJORS,
  CREATE_USER,
} from "@/store/action.type"
import { formValidateError } from "@/common/helper"

export default {
  name: "FormCreateUser",
  props: {
    user: {
      type: Object,
      required: true,
    },
    roles: {
      required: true,
      type: Array,
    },
  },
  data() {
    return {
      formData: {
        username: "",
        first_name: "",
        last_name: "",
        faculty_id: "",
        major_id: "",
      },
      rules: {
        username: "required|max:50",
        first_name: "required|max:50",
        last_name: "required|max:50",
        faculty_id: "",
        major_id: "",
        selectRoles: "required",
      },
      under_faculty: false,
      under_major: false,
      option_majors: [],
      selectRoles: [],
      isLoading: false,
    }
  },
  computed: {
    ...mapGetters(["years", "faculties", "majors", "userError"]),
  },
  watch: {
    user() {
      this.initUser()
    },
  },
  mounted() {
    this.$store.dispatch(GET_YEARS)
    this.$store.dispatch(GET_FACULTIES)
    this.$store.dispatch(GET_MAJORS)

    this.initUser()
  },
  methods: {
    initUser() {
      this.formData.username = this.user.username
      this.formData.first_name = this.user.first_name
      this.formData.last_name = this.user.last_name
    },
    onUserRoleChange(roles) {
      const hasFacultyManager = roles.includes(2)
      const hasCourseManager = roles.includes(3)

      this.under_faculty = hasFacultyManager || hasCourseManager
      this.under_major = hasCourseManager

      this.rules.faculty_id = hasFacultyManager || hasCourseManager ? "required" : ""
      this.rules.major_id = hasCourseManager ? "required" : ""

      if (!hasFacultyManager) {
        this.formData.faculty_id = ""
      }

      if (!hasCourseManager) {
        this.formData.major_id = ""
      }
    },
    onFacultyChange(faculty_id) {
      this.option_majors = this.majors.filter(
        (major) => major.faculty_id === faculty_id
      )
    },
    onCancel() {
      this.$emit("onCancel", false)
    },
    initUserRole() {
      this.formData.roles = this.selectRoles.map(
        (selectRole) => this.roles[selectRole].id
      )
    },
    async submitCreateUser() {
      this.isLoading = true

      const validated = await this.$refs.observer.validate()

      if (validated) {
        this.initUserRole()

        await this.$store.dispatch(CREATE_USER, this.formData)

        if (!_.isEmpty(this.userError)) {
          formValidateError(this, this.userError)
        } else {
          this.$refs.form.reset()
          this.onCancel()
        }
      }

      this.isLoading = false
    },
  },
}
</script>